import { Injectable, signal } from '@angular/core';
import { UserRoles } from '@features/auth/user-roles';
import { BehaviorSubject, Subject } from 'rxjs';

export interface IMenuItem {
  title?: string;
  margin?: string;
  permissions?: UserRoles[];
  permission?: any;
  navigationMethod?: string;
  routerState?: string;
  queryParams?: object;
  iconClass?: string;
  linkClass?: string;
  count?: number;
  count$?: BehaviorSubject<number>;
  hasMenu?: boolean;
  menuComponentName?: string;
  integrationId?: string;
  groupName?: string;
  show?: boolean;
  show$?: BehaviorSubject<boolean>;
  open?: boolean;
  items?: IMenuItem[];
}

export interface IMenuGroup {
  name?: string;
  show?: boolean;
  show$?: BehaviorSubject<boolean>;
  open?: boolean;
  open$?: BehaviorSubject<boolean>;
  visible?: boolean;
  key?: string;
  items?: Map<string, IMenuItem>;
  permissions?: UserRoles[];
}

@Injectable({ providedIn: 'root' })
export class MenuService {
  isMinimized = new Subject();
  minimizeMenu = new Subject<boolean>();
  isMenuMinimized = signal(false);
  isMinimizedEnabled = signal(false);
  canMinimize = signal(true);

  menu: Map<string, IMenuGroup> = new Map();
  $menu: BehaviorSubject<any> = new BehaviorSubject([]);

  private convertMenu() {
    const newMenu = [];
    const menu = Array.from(this.menu.values());

    menu.forEach((groupItem) => {
      const newItem = {
        name: groupItem.name,
        show: groupItem.show,
        show$: groupItem.show$,
        key: groupItem.key,
        open: groupItem.open,
        open$: groupItem.open$,
        visible: groupItem.visible,
        items: [],
        permissions: groupItem.permissions
      };

      const menus = Array.from(groupItem.items.values());

      if (menus.length) {
        menus.forEach((item) => {
          item.items = [];
        });

        menus.forEach((item) => {
          if (!item.hasOwnProperty('parentKey')) {
            newItem.items.push(item);
          } else {
            const parentItem = newItem.items.find((parent) => {
              // @ts-ignore
              return parent.key === item.parentKey;
            });

            parentItem.items.push(item);
          }
        });
      }

      newMenu.push(newItem);
    });

    return newMenu;
  }

  addGroup(groupName: string, item: IMenuGroup): MenuService {
    if (this.menu.has(groupName)) {
      alert('Group "' + groupName + '" already exist!');
    } else {
      this.menu.set(groupName, item);
    }

    return this;
  }

  setGroup(groupName: string, item: IMenuGroup): MenuService {
    if (!this.menu.has(groupName)) {
      alert('Menu group "' + groupName + '" does not exist!');
    } else {
      const oldMenuGroup = this.menu.get(groupName);
      this.menu.set(groupName, { ...oldMenuGroup, ...item });
    }

    return this;
  }

  addMenu(groupName: string, menuName: string, item: IMenuItem): MenuService {
    const group = this.menu.get(groupName);

    if (group.items.has(menuName)) {
      alert('Menu "' + groupName + '" has "' + menuName + '" item already!');
    }

    group.items.set(menuName, { ...item, groupName });
    return this;
  }

  updateMenu(menuName: string, item: IMenuGroup): MenuService {
    const menu = this.menu.get(menuName);
    if (!menu) {
      alert(`Menu ${menuName} does not exist!`);
    }

    this.menu.set(menuName, item);

    return this;
  }

  setMenu(groupName: string, menuName: string, item: IMenuItem): MenuService {
    const group = this.menu.get(groupName);

    if (!group.items.has(menuName)) {
      alert('Menu "' + groupName + '" has "' + menuName + '" does not exist!');
    } else {
      const oldMenu = group.items.get(menuName);
      group.items.set(menuName, { ...oldMenu, ...item });
    }

    return this;
  }

  applyMenu(): void {
    this.$menu.next(this.convertMenu());
  }

  minimize(): void {
    if (this.isMinimizedEnabled()) {
      this.isMenuMinimized.set(true);
      document.body.classList.add('minimized');
    }
  }

  uNminimize(): void {
    if (this.isMinimizedEnabled()) {
      this.isMenuMinimized.set(false);
      document.body.classList.remove('minimized');
    }
  }

  handleClickOutside(target: HTMLElement): void {
    const clickedInside = document.getElementById('main-sidebar').contains(target);
    if (!clickedInside) {
      this.minimize();
    }
  }
}
